import { useEffect, useState } from 'react'
import { AlbumFile } from '@/models/albumFile.ts'
import { AlbumFileLike } from '@/models/albumFileLike.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { useClient } from '@/stores/useClient.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import { captureException } from '@/controller/sentryHelper.ts'
import { saveAs } from '@/controller/fileSaver.ts'
import { shareDownloadURL, shareDownloadedFileByUrl } from '@/controller/updateHelper.ts'
import { resolveDriveDownloadQualities, resolveSingleFileQuality } from '@/models/downloadOptions.ts'
import { useTrackStat } from '@/contexts/statsScopeContext.tsx'

// Files above this size cannot be shared as a blob via the Web Share API and
// are downloaded / shared by URL instead. Mirrors FilesSlideshow.
const SHARE_DOWNLOAD_LIMIT = 1024 * 1024 * 25

export interface FileActionsState {
  /** The current user's like on this file, if any. */
  yourLike: AlbumFileLike | null
  likeCount: number
  /** Whether the like button should be interactive (logged in, not a demo file). */
  canLike: boolean
  isLiking: boolean
  toggleLike: () => void
  hideDownload: boolean
  isDownloading: boolean
  download: () => void
  /** Whether the Web Share API is available. */
  canShare: boolean
  hideShare: boolean
  isSharing: boolean
  share: () => void
}

/**
 * Per-file like / download / share actions for the gallery grid.
 * Reuses the exact same client functions and helpers as the FilesSlideshow
 * lightbox, but keeps a local copy of the likes so the heart updates
 * immediately and stays correct regardless of how the parent stores files.
 */
export function useFileActions(file: AlbumFile, albumId?: string): FileActionsState {
  const { profile } = useProfile()
  const { like, unlike } = useClient()
  const { currentDrive } = useDrives()
  const filesContext = useFilesContext()
  const track = useTrackStat()

  const [likes, setLikes] = useState<AlbumFileLike[]>(file.likes ?? [])
  const [isLiking, setIsLiking] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)

  // Keep local like state in sync when the file is updated elsewhere (e.g. lightbox).
  useEffect(() => {
    setLikes(file.likes ?? [])
  }, [file.likes])

  const yourLike = profile ? likes.find(l => l.author === profile.username) ?? null : null
  const canLike = profile != null && !file.is_demo

  const hideDownload = !!currentDrive?.is_hide_download
  const hideShare = !!currentDrive?.is_hide_share
  const canShare = typeof navigator !== 'undefined' && !!navigator.share
  // Which variant the tile's download button hands out (no picker for single files).
  const downloadQuality = resolveSingleFileQuality(
    resolveDriveDownloadQualities(currentDrive),
    currentDrive?.use_details_dl,
  )

  async function toggleLike() {
    if (isLiking || !profile || file.is_demo) {
      return
    }

    setIsLiking(true)
    try {
      const res = yourLike ? await unlike(file, yourLike) : await like(file)
      const updated = res.data as AlbumFile
      setLikes(updated.likes ?? [])
      filesContext?.updateFile(updated)
    } catch (e) {
      captureException(e, 'toggling like from grid')
      console.error(e)
    } finally {
      setIsLiking(false)
    }
  }

  // Mirrors FilesSlideshow.onDownloadClick.
  async function download() {
    if (isDownloading) {
      return
    }

    if (downloadQuality === 'high' || file.is_demo) {
      // Detail-only: the web-optimized version on every path, including the iOS
      // Web-Share branch (which shares the first argument, not the alternate link).
      const detailUrl = file.detail ?? file.file
      await shareDownloadURL(detailUrl, file.file_name, setIsDownloading, detailUrl)
      return
    }

    if (file.size > SHARE_DOWNLOAD_LIMIT) {
      await saveAs(`${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${file.id}/download/`, file.file_name, setIsDownloading)
    } else {
      await shareDownloadURL(file.file, file.file_name, setIsDownloading, `${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${file.id}/download/`)
    }
  }

  // Mirrors FilesSlideshow.onShareClick decision logic.
  async function share() {
    if (isSharing || !canShare) {
      return
    }

    // Sharing a single image creates nothing on the server, so this is the only
    // place it can be counted (unlike a share link, which the backend counts).
    track('file_shared', { album: albumId, file: file.id })

    if (file.size <= SHARE_DOWNLOAD_LIMIT) {
      await shareDownloadedFileByUrl(file.file, file.file_name, setIsSharing)
    } else if (file.detail && file.detail_size && file.detail_size <= SHARE_DOWNLOAD_LIMIT) {
      await shareDownloadedFileByUrl(file.detail, file.file_name, setIsSharing)
    } else {
      await navigator.share({ url: file.file })
    }
  }

  return {
    yourLike,
    likeCount: likes.length,
    canLike,
    isLiking,
    toggleLike,
    hideDownload,
    isDownloading,
    download,
    canShare,
    hideShare,
    isSharing,
    share,
  }
}
